import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette } from "lucide-react";
import { api } from "@/lib/api";
import { Card, Field, TextInput } from "./kit";
import { applyTheme, normalizeTheme, HEADING_FONTS, BODY_FONTS, PRESETS } from "@/lib/theme";

const COLOR_SLOTS = [
  { key: "primary", label: "Primary (buttons & CTAs)" },
  { key: "accent", label: "Accent (cyan highlights)" },
  { key: "bg_dark", label: "Background Dark (hero & dark sections)" },
  { key: "bg_light", label: "Background Light (white sections)" },
  { key: "heading_text", label: "Heading Text" },
  { key: "body_text", label: "Body Text" },
];

export default function SiteThemePanel() {
  const [theme, setTheme] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    api.get("/content")
      .then(({ data }) => {
        const t = normalizeTheme(data.theme);
        setTheme(t);
        setDraft(t);
        applyTheme(t);
      })
      .catch(() => {});
  }, []);

  if (!theme || !draft) return null;

  const fontOptions = (list, current) => (list.includes(current) ? list : [current, ...list]);

  const setFont = (key, value) => setDraft({ ...draft, [key]: value });

  const setColors = (colors) => setDraft({ ...draft, colors: { ...draft.colors, ...colors } });

  const saveFonts = async () => {
    try {
      const next = { ...theme, heading_font: draft.heading_font, body_font: draft.body_font };
      await api.put("/admin/content/theme", { data: next });
      setTheme(next);
      applyTheme(next);
      toast.success("Fonts applied across the entire website");
    } catch {
      toast.error("Save failed. Please try again.");
    }
  };

  const saveColors = async () => {
    try {
      const next = { ...theme, colors: draft.colors };
      await api.put("/admin/content/theme", { data: next });
      setTheme(next);
      applyTheme(next);
      toast.success("Colors applied across the entire website");
    } catch {
      toast.error("Save failed. Please try again.");
    }
  };

  const loadPreset = (i) => {
    setColors(PRESETS[i].colors);
    toast.success(`${PRESETS[i].name} loaded — press Save Colors to apply`);
  };

  return (
    <div className="space-y-6" data-testid="admin-site-theme">
      <Card title="Site Theme — Fonts" desc="Choose the site-wide heading and body fonts. The preview below updates as you pick. Saving applies the fonts across every page and section.">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Heading font">
            <select className="admin-input" value={draft.heading_font} onChange={(e) => setFont("heading_font", e.target.value)} data-testid="theme-heading-font">
              {fontOptions(HEADING_FONTS, draft.heading_font).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Body font">
            <select className="admin-input" value={draft.body_font} onChange={(e) => setFont("body_font", e.target.value)} data-testid="theme-body-font">
              {fontOptions(BODY_FONTS, draft.body_font).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-4 rounded-xl border border-white/10 bg-[#0A0F2C] p-5" data-testid="theme-font-preview">
          <div style={{ fontFamily: `'${draft.heading_font}', sans-serif`, fontWeight: 800, fontSize: 21, color: "#fff", letterSpacing: "-0.01em" }}>
            Your team, ready for the work ahead.
          </div>
          <p style={{ fontFamily: `'${draft.body_font}', sans-serif`, fontSize: 14, color: "#cbd5e1", marginTop: 8, lineHeight: 1.6 }}>
            Trained remote specialists who handle the work, so you can stay focused on what matters most.
          </p>
        </div>
        <button className="btn btn-sm btn-primary mt-5" onClick={saveFonts} data-testid="theme-save-fonts">Save Fonts</button>
      </Card>

      <Card title="Site Theme — Colors" desc="Six editable swatches. Each opens a color picker; you can also type an exact hex code. Saving applies all colors across every page and section.">
        <div className="flex flex-wrap gap-2 mb-5">
          {PRESETS.map((p, i) => (
            <button key={p.name} className="btn btn-sm btn-outline-light flex items-center gap-2" onClick={() => loadPreset(i)} data-testid={`theme-preset-${i}`}>
              <Palette size={13} /> {p.name}
              <span className="flex gap-1">
                <span className="w-3 h-3 rounded-full border border-white/30" style={{ background: p.colors.primary }} />
                <span className="w-3 h-3 rounded-full border border-white/30" style={{ background: p.colors.accent }} />
                <span className="w-3 h-3 rounded-full border border-white/30" style={{ background: p.colors.bg_dark }} />
              </span>
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {COLOR_SLOTS.map((s) => (
            <Field key={s.key} label={s.label}>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={draft.colors[s.key]}
                  onChange={(e) => setColors({ [s.key]: e.target.value })}
                  className="w-11 h-11 rounded-lg border border-white/15 bg-transparent cursor-pointer p-1"
                  data-testid={`theme-color-${s.key}`}
                />
                <TextInput
                  value={draft.colors[s.key]}
                  onChange={(e) => setColors({ [s.key]: e.target.value })}
                  placeholder="#1D6FF2"
                  data-testid={`theme-hex-${s.key}`}
                />
              </div>
            </Field>
          ))}
        </div>
        <button className="btn btn-sm btn-primary mt-6" onClick={saveColors} data-testid="theme-save-colors">Save Colors</button>
      </Card>
    </div>
  );
}
